import { Component, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { BaseComponent } from '../../../core/base.component';
import { UntypedFormBuilder, UntypedFormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { HeaderService } from '../../../core/services/header.service';
import { Title } from '@angular/platform-browser';
import { NotificationService } from '../../../notification/notification.service';
import { AuthService } from '../../../core/security/auth.service';
import { RolesEnum } from '../../../core/security/roles.enum';
import { CurrentUser, LookupModel, PbxAccountDevicesListModel, PbxAccountModel } from '../../../api/models';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { LocalPbxService } from '../../local-pbx.service';
import { PhoneNumbersUtils } from '../../../core/utilities/phone-numbers-helpers';
import { RequestE911AddressComponent } from './request-e911-address.component';
import { YEALINK_DEVICES_COMPONENTS } from './device-buttons-settings-edit-dialog/constants/yealink-devices-components';
import { POLYCOM_DEVICES_COMPONENTS } from './device-buttons-settings-edit-dialog/constants/polycom-devices-components';
import { DeviceBaseComponent } from './device-buttons-settings-edit-dialog/devices/device-base/device-base.component';
import { BehaviorSubject, finalize, take } from 'rxjs';
import { MatCard, MatCardTitle, MatCardContent } from '@angular/material/card';
import { NgIf, AsyncPipe } from '@angular/common';
import { MatTextInputComponent } from '../../../editors/mat-text-input/mat-text-input.component';
import { Message } from 'primeng/message';
import { MatMultiselectComponent } from '../../../editors/mat-multiselect-input/mat-multiselect.component';
import { MatSimpleSelectComponent } from '../../../editors/mat-simple-select/mat-simple-select.component';
import { MatCheckboxInputComponent } from '../../../editors/mat-checkbox-input/mat-checkbox-input.component';
import { MatPasswordInputComponent } from '../../../editors/mat-password-input/mat-password-input.component';
import {
  MatTable,
  MatColumnDef,
  MatHeaderCellDef,
  MatHeaderCell,
  MatCellDef,
  MatCell,
  MatHeaderRowDef,
  MatHeaderRow,
  MatRowDef,
  MatRow,
} from '@angular/material/table';
import { MatTooltip } from '@angular/material/tooltip';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { Angular2PromiseButtonModule } from 'angular2-promise-buttons';
import { MacAddressPipe } from '../../../core/pipes/mac-address.pipe';

@Component({
  selector: 'app-edit-pbx-account',
  templateUrl: './edit-pbx-account.component.html',
  styleUrls: ['./edit-pbx-account.component.scss'],
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatCard,
    MatCardTitle,
    MatCardContent,
    NgIf,
    MatTextInputComponent,
    Message,
    MatMultiselectComponent,
    MatSimpleSelectComponent,
    MatCheckboxInputComponent,
    MatPasswordInputComponent,
    MatTable,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCellDef,
    MatCell,
    MatTooltip,
    MatButton,
    MatIcon,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    Angular2PromiseButtonModule,
    AsyncPipe,
    MacAddressPipe,
  ],
})
export class EditPbxAccountComponent extends BaseComponent implements OnInit, OnChanges, OnDestroy {
  form: UntypedFormGroup;
  submitted: boolean;
  serverError: boolean;
  editModel: PbxAccountModel = {
    id: null,
    accountType: 'extensions',
    firstName: '',
    fullName: '',
    displayName: '',
    name: '',
  };

  private paramsSubscription;
  private id: string;

  e911Addresses: LookupModel[] = [];
  accountDevicesList: PbxAccountDevicesListModel[] = [];
  displayedColumns: string[] = ['mac', 'vendor', 'model', 'actions'];

  buttonSubmitPromise: Promise<void>;

  cellConnExplanation = `
  If call forwarding to a cell number is enabled:<br><br>
  1. When answering an incoming call on your cell, you’ll hear the following announcement: "A call is coming from the PBX, press 1 to accept."<br><br>
  2. If you don't press 1 to accept the call, the call will be forwarded to your PBX voicemail
  `;

  public readonly isButtonsAvailable$ = new BehaviorSubject<boolean>(false);

  constructor(
    route: ActivatedRoute,
    headerService: HeaderService,
    titleService: Title,
    authService: AuthService,
    public dialog: MatDialog,
    public pbxService: LocalPbxService,
    private fb: UntypedFormBuilder,
    private notificationService: NotificationService,
  ) {
    super(route, headerService, titleService, authService);

    this.paramsSubscription = this.route.params.subscribe(params => {
      this.id = params['id'];
      console.log(this.id);
    });

    this.headerService.showBackButton(true);
    this.createForm();
  }

  onCurrentUserChanged(user: CurrentUser) {
    super.onCurrentUserChanged(user);

    if (user) {
      this.loadAccountDevicesList(user);

      this.hasEditAccess = this.authService.hasAccess([
        RolesEnum.PbxAccounts_Edit,
        RolesEnum.PbxAccounts_EditOwn,
        RolesEnum.PbxAccounts_Delete,
      ]);
    } else {
      this.hasEditAccess = false;
    }
  }

  createForm(): void {
    const controlsConfig: any = {
      extension: ['', []],
      firstName: ['', Validators.required],
      lastName: ['', []],
      cellDis: [[], []],
      cellConn: [false, []],
      vpaEnabled: [false, []],
      eAni: ['', []],
      cellTime: ['', []],
      cellHunt: ['', []],
      cellAcd: ['', []],
      mbEnable: ['', []],
      mbTimeout: ['', []],
      mailboxGroup: [[], []],
      emailAddress: ['', Validators.email],
      mbPin: ['', [Validators.pattern('^(notChanged)$|^[0-9]*$')]],
      // 'mbPinConfirm': ['', []],
      emailVmail: ['none', []],
      vmailOption: ['keep', []],
      emailMissed: [false, []],
      emailAll: [false, []],
      emailMbFull: [false, []],
      cfa: [[], []],
      cfaNumbers: ['', []],
      cfaTimes: ['', []],
      cfb: [[], []],
      cfbNumbers: ['', []],
      cfbTimes: ['', []],
      cfn: [[], []],
      cfnNumbers: ['', []],
      cfnTimes: ['', []],
      cfd: [[], []],
    };

    this.form = this.fb.group(controlsConfig, {
      //validators: matchFields('mbPin', 'mbPinConfirm')
    });
  }

  async onSubmit() {
    this.serverError = false;

    if (this.form.valid && this.hasEditAccess) {
      this.buttonSubmitPromise = this.processSubmit();
    } else {
      this.notificationService.notifyDanger('Form is not valid');
    }
  }

  async processSubmit(): Promise<void> {
    try {
      this.submitted = true;

      const reqData = this.prepareFormModel();
      this.editModel = await this.pbxService.savePbxAccount(reqData);
      this.notificationService.notifySuccess('Extension updated successfully');
      this.ngOnChanges();
      //this.headerService.goBack();
    } catch (e) {
      this.serverError = true;
    } finally {
      this.submitted = false;
    }
  }

  prepareFormModel(): PbxAccountModel {
    const formModel = this.form.value;

    const resultModel: PbxAccountModel = {
      id: this.editModel.id,
      accountType: this.editModel.accountType,
      firstName: formModel.firstName,
      displayName: formModel.lastName,
      cellDis: formModel.cellDis.map(x => PhoneNumbersUtils.phoneNumberToNational(x)),
      cellConn: formModel.cellConn,
      eAni: formModel.eAni != '-1' ? formModel.eAni : '',
      noVpa: !formModel.vpaEnabled,
      cellTime: formModel.cellTime,
      cellHunt: formModel.cellHunt,
      cellAcd: formModel.cellAcd,
      mbEnable: formModel.mbEnable,
      mbTimeout: formModel.mbTimeout,
      mailboxGroup: formModel.mailboxGroup,
      emailAddress: formModel.emailAddress,
      mbPin: formModel.mbPin,
      emailVmail: formModel.emailVmail,
      vmailOption: formModel.vmailOption,
      emailMissed: formModel.emailMissed,
      emailAll: formModel.emailAll,
      emailMbFull: formModel.emailMbFull,
      cfa: formModel.cfa ? formModel.cfa[0] : '',
      cfaNumbers: formModel.cfaNumbers,
      cfaTimes: formModel.cfaTimes,
      cfb: formModel.cfb ? formModel.cfb[0] : '',
      cfbNumbers: formModel.cfbNumbers,
      cfbTimes: formModel.cfbTimes,
      cfn: formModel.cfn ? formModel.cfn[0] : '',
      cfnNumbers: formModel.cfnNumbers,
      cfnTimes: formModel.cfnTimes,
      cfd: formModel.cfd ? formModel.cfd[0] : '',
    };

    console.log('prepareFormModel', resultModel);

    return resultModel;
  }

  async ngOnInit() {
    super.ngOnInit();

    this.loadPbxAccountById();
    this.loadPbxE911Addresses();

    await this.pbxService.getLookupLists();

    this.createForm();
    this.ngOnChanges();
  }

  ngOnChanges(changes?: SimpleChanges): void {
    const defValues: any = {
      extension: this.editModel.name,
      firstName: this.editModel.firstName,
      lastName: this.editModel.displayName,
      eAni: this.editModel.eAni,
      cellDis: this.editModel.cellDis,
      cellConn: this.editModel.cellConn,
      vpaEnabled: !this.editModel.noVpa,
      cellTime: this.editModel.cellTime,
      cellHunt: this.editModel.cellHunt,
      cellAcd: this.editModel.cellAcd,
      mbEnable: this.editModel.mbEnable,
      mbTimeout: this.editModel.mbTimeout,
      mailboxGroup: this.editModel.mailboxGroup,
      emailAddress: this.editModel.emailAddress,
      mbPin: this.editModel.mbPin,
      //'mbPinConfirm': this.editModel.mbPin,
      emailVmail: this.editModel.emailVmail,
      vmailOption: this.editModel.vmailOption,
      emailMissed: this.editModel.emailMissed,
      emailAll: this.editModel.emailAll,
      emailMbFull: this.editModel.emailMbFull,
      cfa: this.editModel.cfa ? [this.editModel.cfa] : [],
      cfaNumbers: this.editModel.cfaNumbers,
      cfaTimes: this.editModel.cfaTimes,
      cfb: this.editModel.cfb ? [this.editModel.cfb] : [],
      cfbNumbers: this.editModel.cfbNumbers,
      cfbTimes: this.editModel.cfbTimes,
      cfn: this.editModel.cfn ? [this.editModel.cfn] : [],
      cfnNumbers: this.editModel.cfnNumbers,
      cfnTimes: this.editModel.cfnTimes,
      cfd: this.editModel.cfd ? [this.editModel.cfd] : [],
    };

    this.form.reset(defValues);
  }

  ngOnDestroy() {
    super.ngOnDestroy();
    this.paramsSubscription?.unsubscribe();
    this.headerService.showBackButton(false);
  }

  /*async onDeleteClick() {
    const dialogRef = this.dialog.open(MatConfirmDialogComponent, {
      // width: '250px',
      data: {
        title: 'Delete Pbx Account',
        text: 'Are you sure you want to delete account on pbx?',
        actionTitle: 'Delete',
      }
    });

    dialogRef.afterClosed().subscribe(async (result) => {
      if (result) {
        await this.onDeleteProcess();
      }
    });
  }

  async onDeleteProcess(): Promise<void> {
    await this.pbxService.deletePbxAccount(this.editModel.id);
    await this.notificationService.notifySuccess('Role deleted successfully');
    await this.headerService.goToComponent(PbxGroupsComponent.name);
  }*/

  specialMenuChanged($event: any) {
    if ($event) {
      this.form.patchValue({
        mbTimeout: '60',
      });
    }
  }

  onSelectedE911AddressChanged($event: any) {
    if ($event === '-1') {
      this.dialog.open(RequestE911AddressComponent, {
        width: '400px',
        data: {
          pbxAccount: this.editModel,
        },
      });
    }
  }

  public isDeviceEditDisabled(device: PbxAccountDevicesListModel): boolean {
    return !!!device.model || !!!device.vendor;
  }

  public onEditDeviceButtonsSettings(device: PbxAccountDevicesListModel): void {
    if (this.isDeviceEditDisabled(device)) {
      return;
    }

    const selectedComponent = this.getComponentByVendorAndModel(device?.vendor, device?.model);
    const dialogConfig: MatDialogConfig = {
      width: '90%',
      height: '90%',
      data: { deviceId: device.id, extension: this.editModel },
    };

    if (!selectedComponent) {
      this.dialog.open(DeviceBaseComponent, { ...dialogConfig, data: { ...dialogConfig.data, isDefault: true } });

      return;
    }

    this.dialog.open(selectedComponent, dialogConfig);
  }

  private getComponentByVendorAndModel(vendor: string, model: string): any {
    if (!vendor || !model) {
      return null;
    }

    switch (vendor) {
      case 'Yealink':
        return YEALINK_DEVICES_COMPONENTS[model.slice(0, 3)];
      case 'Polycom':
        return POLYCOM_DEVICES_COMPONENTS[model];
      default:
        return null;
    }
  }

  private loadPbxAccountById(): void {
    this.pbxService
      .getPbxAccountById(this.id)
      .pipe(take(1))
      .subscribe({
        next: editModel => (this.editModel = editModel),
      });
  }

  private loadPbxE911Addresses(): void {
    this.pbxService
      .getPbxE911Addresses()
      .pipe(take(1))
      .subscribe(e911AddressList => {
        this.e911Addresses = e911AddressList.map(value => {
          return {
            id: value.telephoneNumber,
            text: value.fullAddress,
          };
        });
        this.e911Addresses.push({
          id: '-1',
          text: 'Request New E-911 Address',
        });
      });
  }

  private loadAccountDevicesList(user: CurrentUser): void {
    const isAvailable = user.serverVersion === 'V6';

    if (isAvailable) {
      this.pbxService
        .getPbxAccountDevicesList(this.id)
        .pipe(take(1))

        .subscribe({
          next: devicesList => (this.accountDevicesList = devicesList || []),
        });
    }

    this.isButtonsAvailable$.next(isAvailable);
  }
}
